import { Description } from '../Description';
import { ErrorMessage } from '../ErrorMessage';
import { Label } from '../Label';
import { VisuallyHidden } from '../../display/VisuallyHidden';
import type { FieldProps } from '../../../types';
import { Icon } from '@zapier/zinnia-icons';
import { forwardRef } from 'react';
import type { TextFieldProps as ReactAriaTextFieldProps } from 'react-aria-components';
import {
  Input as ReactAriaInput,
  TextArea as ReactAriaTextArea,
  TextField as ReactAriaTextField,
} from 'react-aria-components';

import { mergeClassNames } from '../../../utils/mergeClassNames';
import styles from './TextField.module.scss';

export type BetaTextFieldProps = Omit<ReactAriaTextFieldProps, 'children'> &
  FieldProps & {
    /**
     * Whether the input accepts multiple lines of text. Renders a `textarea` when `true`.
     *
     * @default false
     *  */
    isMultiline?: boolean;

    /** Placeholder text for the input. */
    placeholder?: string;

    /** Indicates in which directions a textarea can be resized. Only applies when `isMultiline` is `true`. */
    resize?: 'horizontal' | 'vertical' | 'both' | 'none';

    /** Number of visible text lines for a textarea. Only applies when `isMultiline` is `true`. */
    rows?: number;

    /**
     * The size of the input.
     *
     * @default 'medium'
     */
    size?: 'compact' | 'medium' | 'large';
  };

/**
 * A text field allows a user to enter a plain text value with a keyboard.
 * Built using React Aria's
 * [TextField](https://react-spectrum.adobe.com/react-aria/TextField.html)
 * component.
 *
 * ```tsx
 * <BetaTextField label="Name" placeholder="Enter your name" />
 * ```
 */
export const BetaTextField = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  BetaTextFieldProps
>(
  (
    {
      className,
      errorMessage,
      helpText,
      isDisabled = false,
      isInvalid = false,
      isLabelHidden = false,
      isMultiline = false,
      label,
      placeholder,
      resize,
      rows,
      size = 'medium',
      ...props
    },
    ref
  ) => {
    return (
      <ReactAriaTextField
        {...props}
        className={(renderProps) =>
          mergeClassNames([styles.root, className], renderProps)
        }
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        data-zds
      >
        {label &&
          (isLabelHidden ? (
            <VisuallyHidden>
              <Label>{label}</Label>
            </VisuallyHidden>
          ) : (
            <Label
              isCompact={size === 'compact'}
              isDisabled={isDisabled}
              isInvalid={isInvalid}
              isRequired={props.isRequired}
            >
              {isInvalid && (
                <Icon
                  color="StatusErrorStronger"
                  isBlock
                  name="formXCircle"
                  size={18}
                />
              )}
              {label}
            </Label>
          ))}

        {isMultiline ? (
          <ReactAriaTextArea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            className={styles.input}
            data-size={size}
            data-multiline
            data-resize={resize}
            data-zds
            placeholder={placeholder}
            rows={rows}
          />
        ) : (
          <ReactAriaInput
            ref={ref as React.Ref<HTMLInputElement>}
            className={styles.input}
            data-size={size}
            data-zds
            placeholder={placeholder}
          />
        )}

        {isInvalid && errorMessage ? (
          <ErrorMessage isCompact={size === 'compact'}>
            {errorMessage}
          </ErrorMessage>
        ) : (
          helpText && (
            <Description isCompact={size === 'compact'}>{helpText}</Description>
          )
        )}
      </ReactAriaTextField>
    );
  }
);

BetaTextField.displayName = 'BetaTextField';
